import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Loader2 } from 'lucide-react';
import { TextInput, TextArea, SelectInput, MultiSelectChips } from '../FormElements';
import { FileUpload } from './FileUpload';
import { RangeSlider } from './RangeSlider';
interface OpportunityFormProps {
  role: 'brand' | 'organizer';
  onComplete: (data: any) => void;
  onCancel: () => void;
}
export function OpportunityForm({
  role,
  onComplete,
  onCancel
}: OpportunityFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    type: '',
    location: '',
    date: '',
    budget: 5000,
    description: '',
    requirements: '',
    tags: [] as string[]
  });
  const accentColor = role === 'brand' ? 'cyan' : 'purple';
  const buttonColor = role === 'brand' ? 'bg-cyan-500 hover:bg-cyan-400 text-slate-900' : 'bg-purple-600 hover:bg-purple-500 text-white';
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSubmitting(false);
    onComplete(formData);
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="bg-slate-900 border border-slate-800 rounded-2xl p-8" data-id="element-374">
      <div className="mb-8" data-id="element-375">
        <h2 className="text-2xl font-bold text-white mb-2" data-id="element-376">
          {role === 'brand' ? 'Add a Campaign / Product' : 'Add an Event / Opportunity'}
        </h2>
        <p className="text-slate-400" data-id="element-377">
          {role === 'brand' ? 'Tell us what you want to promote so we can find the right events.' : "Tell us about the specific opportunity you're offering to sponsors."}
        </p>
      </div>

      <form onSubmit={handleSubmit} data-id="element-378">
        <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-379">
          <TextInput label={role === 'brand' ? 'Campaign / Product Name' : 'Event Name'} placeholder={role === 'brand' ? 'e.g. Summer Product Launch' : 'e.g. Tech Summit 2024'} value={formData.title} onChange={e => setFormData({
          ...formData,
          title: e.target.value
        })} required data-id="element-380" />
          <SelectInput label={role === 'brand' ? 'Campaign Type' : 'Opportunity Type'} value={formData.type} onChange={e => setFormData({
          ...formData,
          type: e.target.value
        })} options={role === 'brand' ? ['Product Launch', 'Brand Awareness', 'Sampling', 'Lead Gen', 'Content Creation'] : ['Title Sponsorship', 'Booth/Exhibition', 'Speaking Slot', 'Digital Package', 'VIP Experience']} required data-id="element-381" />
        </div>

        <FileUpload label={role === 'brand' ? 'Product / Campaign Visuals' : 'Event Visuals'} accentColor={accentColor} data-id="element-382" />

        <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-383">
          <TextInput label="Location" placeholder="e.g. London, UK or 'Global'" value={formData.location} onChange={e => setFormData({
          ...formData,
          location: e.target.value
        })} required data-id="element-384" />
          <TextInput label={role === 'brand' ? 'Preferred Timing' : 'Event Date(s)'} placeholder="e.g. Q3 2024 or Oct 15-17" value={formData.date} onChange={e => setFormData({
          ...formData,
          date: e.target.value
        })} required data-id="element-385" />
        </div>

        <div className="mb-8" data-id="element-386">
          <RangeSlider label={role === 'brand' ? 'Budget for this campaign' : 'Sponsorship Value'} min={1000} max={100000} step={1000} defaultValue={formData.budget} formatValue={v => `€${v.toLocaleString()}`} onChange={v => setFormData({
          ...formData,
          budget: v
        })} accentColor={accentColor} data-id="element-387" />
        </div>

        <TextArea label={role === 'brand' ? 'What are you looking for?' : 'What do sponsors get?'} placeholder={role === 'brand' ? 'Describe your ideal activation...' : 'List the specific deliverables and benefits...'} value={formData.description} onChange={e => setFormData({
        ...formData,
        description: e.target.value
      })} required data-id="element-388" />

        <div className="flex justify-end gap-4 mt-8" data-id="element-389">
          <button type="button" onClick={onCancel} className="px-6 py-3 rounded-full font-medium text-slate-400 hover:text-white transition-colors" data-id="element-390">
            Cancel
          </button>
          <button type="submit" disabled={isSubmitting} className={`px-8 py-3 rounded-full font-bold transition-all flex items-center gap-2 ${buttonColor}`} data-id="element-391">
            {isSubmitting ? <>
                <Loader2 size={18} className="animate-spin" data-id="element-392" /> Saving...
              </> : <>
                {role === 'brand' ? 'Find Matches' : 'Publish Opportunity'}{' '}
                <ArrowRight size={18} data-id="element-393" />
              </>}
          </button>
        </div>
      </form>
    </motion.div>;
}